import React, { useState } from 'react';
import {
  Users, ChatCircleText, ShieldCheck, MegaphoneSimple,
  PaintBrush, LinkSimple, DevicesIcon, Bell, IdentificationCard, UsersFour
} from '@phosphor-icons/react';

/**
 * Professional card-grid settings hub. Renders a corporate-style grid of
 * setting sections. Clicking a card expands it inline (accordion) so
 * everything stays on ONE page as the user requested.
 *
 * Props: sections = [{ key, title, subtitle, icon, accent, badge?, render }]
 */
const SettingsHub = ({ sections = [], defaultOpen = null }) => {
  const [openKey, setOpenKey] = useState(defaultOpen);

  return (
    <div data-testid="settings-hub">
      {/* Header */}
      <div className="mb-5">
        <h2 className="text-2xl font-bold text-slate-900">Settings</h2>
        <p className="text-sm text-slate-500 mt-0.5">Choose a section to manage. Everything is one click away.</p>
      </div>

      {/* Card grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {sections.map(s => {
          const isActive = openKey === s.key;
          const Icon = s.icon;
          return (
            <button
              key={s.key}
              onClick={() => setOpenKey(isActive ? null : s.key)}
              data-testid={`settings-card-${s.key}`}
              className={`text-left rounded-xl border transition-all p-4 group flex items-start gap-3 relative overflow-hidden ${
                isActive
                  ? 'border-blue-500 shadow-md bg-white ring-2 ring-blue-100'
                  : 'border-slate-200 hover:border-blue-300 hover:shadow-sm bg-white'
              }`}
            >
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm"
                style={{ background: `${s.accent}15`, color: s.accent }}
              >
                {Icon && <Icon size={20} weight="fill" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 text-sm flex items-center gap-2">
                  <span className="truncate">{s.title}</span>
                  {s.badge != null && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold text-white flex-shrink-0" style={{ background: s.accent }}>{s.badge}</span>
                  )}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{s.subtitle}</div>
              </div>
              <div className={`text-slate-400 group-hover:text-blue-500 text-lg transition-transform ${isActive ? 'rotate-90' : ''}`}>›</div>
            </button>
          );
        })}
      </div>

      {/* Expanded content area */}
      {openKey && (
        <div className="mt-4 rounded-xl border border-slate-200 shadow-sm bg-white overflow-hidden" data-testid={`settings-expanded-${openKey}`}>
          <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {(() => {
                const s = sections.find(x => x.key === openKey);
                const Icon = s?.icon;
                return (
                  <>
                    {Icon && <Icon size={16} className="text-slate-600" weight="fill" />}
                    <span className="text-sm font-semibold text-slate-800">{s?.title}</span>
                  </>
                );
              })()}
            </div>
            <button onClick={() => setOpenKey(null)} className="text-xs text-slate-500 hover:text-red-600 font-semibold">Close ✕</button>
          </div>
          <div className="p-0">
            {sections.find(s => s.key === openKey)?.render?.()}
          </div>
        </div>
      )}
    </div>
  );
};

// Icon presets (re-exported so pages don't need to import from phosphor)
SettingsHub.icons = { Users, ChatCircleText, ShieldCheck, MegaphoneSimple, PaintBrush, LinkSimple, DevicesIcon, Bell, IdentificationCard, UsersFour };

export default SettingsHub;
